"use client";

import Link from "next/link";
import { notFound, useParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useDisplayLocale } from "@/lib/displayLocale";
import { findArticle, HELP_CATEGORIES } from "@/lib/helpArticles";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";

/**
 * EPIC-024 STORY-665 — help article detail page.
 *
 * Articles render from the static registry (lib/helpArticles.ts).
 * No CMS / DB / MDX — closed-beta scope. Founder edits the registry
 * file directly.
 *
 * 2026-05-03 — chrome migrated from a 2-locale (en/pt) `STRINGS`
 * object to the full 5-locale i18n catalog. Article body itself
 * still uses the 2-locale `displayLocale` resolution because the
 * helpArticles registry only stores en + pt; non-en/pt viewers
 * see the English body. Promoting that to 5 locales is queued
 * for a content pass.
 */
export default function HelpArticlePage() {
  const params = useParams<{ slug: string }>();
  const slug = params?.slug;
  const locale = useDisplayLocale();
  const { t } = useTranslation();

  const article = slug ? findArticle(slug) : undefined;
  if (!article) {
    notFound();
  }

  const body = article.body[locale];
  const category = HELP_CATEGORIES.find((c) => c.id === article.category);

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0 max-w-3xl">
          <Link
            href="/help"
            className="text-[12px] text-[var(--color-text3)] hover:text-[var(--color-text)]"
          >
            {t("helpArticle.backToHelp")}
          </Link>

          <article className="mt-3">
            <header>
              {category && (
                <p className="mb-2 flex items-center gap-1 text-[11px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
                  <category.Icon
                    size={12}
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  {category.label[locale]}
                </p>
              )}
              <h1 className="text-[26px] font-extrabold tracking-tight">
                {article.title[locale]}
              </h1>
              <p className="mt-1 text-[11px] text-[var(--color-text3)]">
                {t("helpArticle.updatedLabel")}{" "}
                {new Date(article.updatedAt).toLocaleDateString(
                  locale === "pt" ? "pt-BR" : "en-US",
                )}
              </p>
            </header>

            <div className="mt-6 space-y-5">
              <p className="text-[15px] leading-relaxed text-[var(--color-text2)]">
                {body.intro}
              </p>

              {body.sections.map((section, i) => (
                <section key={i}>
                  <h2 className="mb-2 text-[16px] font-bold">{section.heading}</h2>
                  {section.paragraphs.map((p, j) => (
                    <p
                      key={j}
                      className="mb-3 text-[14px] leading-relaxed text-[var(--color-text2)]"
                    >
                      {p}
                    </p>
                  ))}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="ml-4 list-disc space-y-1 text-[14px] text-[var(--color-text2)]">
                      {section.bullets.map((b, k) => (
                        <li key={k}>{b}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            <div className="mt-8 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-[13px]">
              <Link
                href="/help/new"
                className="font-bold text-[var(--color-brand)] hover:underline"
              >
                {t("helpArticle.fileTicketCta")}
              </Link>
            </div>
          </article>
        </main>
      </div>
    </>
  );
}
